import { FormEvent, useEffect, useMemo, useState } from "react";
import { Loader2, Plus, RefreshCw, Image as ImageIcon, X, ArrowUp, ArrowDown, Star } from "lucide-react";
import AdminLayout from "@/components/admin/AdminLayout";
import MediaPicker from "@/components/admin/MediaPicker";
import { supabase } from "@/integrations/supabase/client";
import { useAdminAuth } from "@/hooks/useAdminAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { toast } from "sonner";

interface PropertyRow {
  id: string;
  title: string;
  slug: string;
  region: string;
  property_type: string | null;
  listing_status: string | null;
  price: string | null;
  price_numeric: number | null;
  location: string | null;
  address_line_1: string | null;
  address_line_2: string | null;
  city: string | null;
  state_region: string | null;
  country: string | null;
  bedrooms: number | null;
  bathrooms: number | null;
  square_feet: number | null;
  lot_size: string | null;
  short_description: string | null;
  description: string | null;
  investment_highlights: string[];
  amenities: string[];
  features: string[];
  hero_image_url: string | null;
  cta_label: string | null;
  cta_url: string | null;
  is_featured: boolean;
  is_published: boolean;
  show_on_homepage: boolean;
  sort_order: number;
}

interface PropertyImageRow {
  id: string;
  property_id: string;
  image_url: string;
  alt_text: string | null;
  caption: string | null;
  sort_order: number;
  is_featured: boolean;
}

type RegionFilter = "all" | "MA" | "DR";
type PubFilter = "all" | "published" | "draft";

const REGIONS = ["MA", "DR"] as const;
const STATUSES = ["available", "coming_soon", "sold", "pending", "under_contract", "featured", "draft"];

const slugify = (s: string) =>
  s.toLowerCase().trim().replace(/[^a-z0-9\s-]/g, "").replace(/\s+/g, "-").replace(/-+/g, "-").slice(0, 100);

const inputToArr = (s: string) =>
  s.split(/\r?\n|,/).map((x) => x.trim()).filter(Boolean);
const arrToInput = (a?: string[] | null) => (a ?? []).join("\n");

const isUrlOrPath = (v: string) => {
  if (v.startsWith("/")) return true;
  try { new URL(v); return true; } catch { return false; }
};

const emptyDraft = (): Partial<PropertyRow> => ({
  title: "", slug: "", region: "DR", property_type: "", listing_status: "available",
  price: "", price_numeric: null, location: "",
  address_line_1: "", address_line_2: "", city: "", state_region: "", country: "",
  bedrooms: null, bathrooms: null, square_feet: null, lot_size: "",
  short_description: "", description: "",
  investment_highlights: [], amenities: [], features: [],
  hero_image_url: "", cta_label: "", cta_url: "",
  is_featured: false, is_published: false, show_on_homepage: false, sort_order: 0,
});

const AdminProperties = () => {
  const { user } = useAdminAuth();
  const [rows, setRows] = useState<PropertyRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [regionFilter, setRegionFilter] = useState<RegionFilter>("all");
  const [pubFilter, setPubFilter] = useState<PubFilter>("all");

  const [editing, setEditing] = useState<Partial<PropertyRow> | null>(null);
  const [slugTouched, setSlugTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [heroPickerOpen, setHeroPickerOpen] = useState(false);
  const [galleryPickerOpen, setGalleryPickerOpen] = useState(false);

  const [highlightsStr, setHighlightsStr] = useState("");
  const [amenitiesStr, setAmenitiesStr] = useState("");
  const [featuresStr, setFeaturesStr] = useState("");

  const [gallery, setGallery] = useState<PropertyImageRow[]>([]);
  const [galleryLoading, setGalleryLoading] = useState(false);

  useEffect(() => {
    document.title = "Properties | Hadassah Homes Admin";
    void load();
  }, []);

  const load = async () => {
    setLoading(true);
    setError(null);
    const { data, error: err } = await supabase
      .from("properties")
      .select("*")
      .order("region", { ascending: true })
      .order("sort_order", { ascending: true })
      .order("title", { ascending: true });
    if (err) setError(err.message);
    else setRows((data ?? []) as unknown as PropertyRow[]);
    setLoading(false);
  };

  const filtered = useMemo(() => {
    return rows.filter((r) => {
      if (regionFilter !== "all" && r.region !== regionFilter) return false;
      if (pubFilter === "published" && !r.is_published) return false;
      if (pubFilter === "draft" && r.is_published) return false;
      return true;
    });
  }, [rows, regionFilter, pubFilter]);

  const loadGallery = async (propertyId: string) => {
    setGalleryLoading(true);
    const { data, error: err } = await supabase
      .from("property_images")
      .select("*")
      .eq("property_id", propertyId)
      .order("sort_order", { ascending: true });
    if (err) toast.error(err.message);
    setGallery(((data ?? []) as unknown as PropertyImageRow[]));
    setGalleryLoading(false);
  };

  const openNew = () => {
    setSlugTouched(false);
    setHighlightsStr(""); setAmenitiesStr(""); setFeaturesStr("");
    setGallery([]);
    setEditing(emptyDraft());
  };
  const openEdit = (row: PropertyRow) => {
    setSlugTouched(true);
    setHighlightsStr(arrToInput(row.investment_highlights));
    setAmenitiesStr(arrToInput(row.amenities));
    setFeaturesStr(arrToInput(row.features));
    setEditing(row);
    void loadGallery(row.id);
  };

  const togglePublish = async (row: PropertyRow) => {
    if (!row.is_published) {
      // Apply same publish validation as the form
      if (!row.title?.trim()) return toast.error("Title is required to publish");
      if (!row.slug?.trim()) return toast.error("Slug is required to publish");
      if (!row.region?.trim()) return toast.error("Region is required to publish");
      if (!row.short_description?.trim() && !row.description?.trim())
        return toast.error("Publishing requires a short description or description");
      if (!row.hero_image_url?.trim()) return toast.error("Publishing requires a hero image");
    }
    const { data, error: err } = await supabase
      .from("properties")
      .update({ is_published: !row.is_published, updated_by: user?.id ?? null } as never)
      .eq("id", row.id)
      .select()
      .single();
    if (err) return toast.error(err.message);
    setRows((prev) => prev.map((r) => (r.id === row.id ? (data as unknown as PropertyRow) : r)));
    toast.success(row.is_published ? "Unpublished" : "Published");
  };

  const toggleFeatured = async (row: PropertyRow) => {
    const { data, error: err } = await supabase
      .from("properties")
      .update({ is_featured: !row.is_featured, updated_by: user?.id ?? null } as never)
      .eq("id", row.id)
      .select()
      .single();
    if (err) return toast.error(err.message);
    setRows((prev) => prev.map((r) => (r.id === row.id ? (data as unknown as PropertyRow) : r)));
  };

  const updateField = <K extends keyof PropertyRow>(k: K, v: PropertyRow[K]) =>
    setEditing((prev) => (prev ? { ...prev, [k]: v } : prev));

  const handleSave = async (e: FormEvent) => {
    e.preventDefault();
    if (!editing) return;
    const title = (editing.title ?? "").trim();
    const slug = (editing.slug ?? "").trim() || slugify(title);
    const region = (editing.region ?? "").trim();
    if (!title) return toast.error("Title is required");
    if (!slug) return toast.error("Slug is required");
    if (!region) return toast.error("Region is required");
    const cta = (editing.cta_url ?? "").trim();
    if (cta && !isUrlOrPath(cta)) return toast.error("CTA URL must be a path (/...) or full URL");

    // Phase 5.1: Stricter validation when publishing. Drafts can save freely.
    const willPublish = !!editing.is_published;
    const willHomepage = !!editing.show_on_homepage;
    const shortDesc = (editing.short_description ?? "").trim();
    const longDesc = (editing.description ?? "").trim();
    const hero = (editing.hero_image_url ?? "").trim();
    const price = (editing.price ?? "").trim();

    if (willPublish) {
      if (!shortDesc && !longDesc)
        return toast.error("Publishing requires a short description or description");
      if (!hero)
        return toast.error("Publishing requires a hero image");
    }
    if (willHomepage) {
      if (!hero) return toast.error("Homepage feature requires a hero image");
      if (!price && !shortDesc)
        return toast.error("Homepage feature requires a price or short description");
    }
    if (willPublish && region === "DR" && editing.id && gallery.length === 0) {
      toast.warning("Tip: DR properties look best with at least one gallery image");
    }

    setSaving(true);
    const payload: Record<string, unknown> = {
      title, slug, region,
      property_type: editing.property_type?.trim() || null,
      listing_status: editing.listing_status?.trim() || null,
      price: editing.price?.trim() || null,
      price_numeric: editing.price_numeric === null || editing.price_numeric === undefined || (editing.price_numeric as unknown as string) === ""
        ? null : Number(editing.price_numeric),
      location: editing.location?.trim() || null,
      address_line_1: editing.address_line_1?.trim() || null,
      address_line_2: editing.address_line_2?.trim() || null,
      city: editing.city?.trim() || null,
      state_region: editing.state_region?.trim() || null,
      country: editing.country?.trim() || null,
      bedrooms: editing.bedrooms == null || (editing.bedrooms as unknown as string) === "" ? null : Number(editing.bedrooms),
      bathrooms: editing.bathrooms == null || (editing.bathrooms as unknown as string) === "" ? null : Number(editing.bathrooms),
      square_feet: editing.square_feet == null || (editing.square_feet as unknown as string) === "" ? null : Number(editing.square_feet),
      lot_size: editing.lot_size?.trim() || null,
      short_description: editing.short_description?.trim() || null,
      description: editing.description?.trim() || null,
      investment_highlights: inputToArr(highlightsStr),
      amenities: inputToArr(amenitiesStr),
      features: inputToArr(featuresStr),
      hero_image_url: editing.hero_image_url?.trim() || null,
      cta_label: editing.cta_label?.trim() || null,
      cta_url: cta || null,
      is_featured: !!editing.is_featured,
      is_published: !!editing.is_published,
      show_on_homepage: !!editing.show_on_homepage,
      sort_order: Number(editing.sort_order ?? 0) || 0,
      updated_by: user?.id ?? null,
    };

    if (editing.id) {
      const { data, error: err } = await supabase
        .from("properties")
        .update(payload as never)
        .eq("id", editing.id)
        .select()
        .single();
      setSaving(false);
      if (err) return toast.error(err.message);
      setRows((prev) => prev.map((r) => (r.id === editing.id ? (data as unknown as PropertyRow) : r)));
      toast.success("Saved");
    } else {
      const { data, error: err } = await supabase
        .from("properties")
        .insert({ ...payload, created_by: user?.id ?? null } as never)
        .select()
        .single();
      setSaving(false);
      if (err) return toast.error(err.message);
      setRows((prev) => [data as unknown as PropertyRow, ...prev]);
      toast.success("Created");
      setEditing(data as unknown as PropertyRow);
      void loadGallery((data as unknown as PropertyRow).id);
    }
  };

  // Gallery operations
  const addGalleryImage = async (url: string) => {
    if (!editing?.id) return toast.error("Save the property before adding images");
    const next = (gallery[gallery.length - 1]?.sort_order ?? -1) + 1;
    const { data, error: err } = await supabase
      .from("property_images")
      .insert({
        property_id: editing.id,
        image_url: url,
        sort_order: next,
        created_by: user?.id ?? null,
        updated_by: user?.id ?? null,
      } as never)
      .select()
      .single();
    if (err) return toast.error(err.message);
    setGallery((prev) => [...prev, data as unknown as PropertyImageRow]);
    toast.success("Image added");
  };

  const removeGalleryImage = async (img: PropertyImageRow) => {
    const { error: err } = await supabase.from("property_images").delete().eq("id", img.id);
    if (err) return toast.error(err.message);
    setGallery((prev) => prev.filter((x) => x.id !== img.id));
    toast.success("Removed from gallery");
  };

  const moveImage = async (img: PropertyImageRow, dir: -1 | 1) => {
    const idx = gallery.findIndex((x) => x.id === img.id);
    const swapIdx = idx + dir;
    if (idx < 0 || swapIdx < 0 || swapIdx >= gallery.length) return;
    const a = gallery[idx];
    const b = gallery[swapIdx];
    const updates = await Promise.all([
      supabase.from("property_images").update({ sort_order: b.sort_order, updated_by: user?.id ?? null } as never).eq("id", a.id),
      supabase.from("property_images").update({ sort_order: a.sort_order, updated_by: user?.id ?? null } as never).eq("id", b.id),
    ]);
    if (updates.some((u) => u.error)) return toast.error("Reorder failed");
    const newGallery = [...gallery];
    newGallery[idx] = { ...a, sort_order: b.sort_order };
    newGallery[swapIdx] = { ...b, sort_order: a.sort_order };
    newGallery.sort((x, y) => x.sort_order - y.sort_order);
    setGallery(newGallery);
  };

  const setImageSortOrder = async (img: PropertyImageRow, sort_order: number) => {
    const { error: err } = await supabase
      .from("property_images")
      .update({ sort_order, updated_by: user?.id ?? null } as never)
      .eq("id", img.id);
    if (err) return toast.error(err.message);
    setGallery((prev) =>
      [...prev.map((x) => (x.id === img.id ? { ...x, sort_order } : x))].sort((a, b) => a.sort_order - b.sort_order),
    );
  };

  return (
    <AdminLayout
      title="Properties"
      description="Manage manual properties and Dominican Republic investment listings. MLS/IDX is separate. Publishing a property can make it visible on the public website if it matches the page filters."
      publicHref="/dominican-republic-investing/properties"
      publicLabel="View DR listings"
    >
      <div className="mx-auto max-w-7xl space-y-5">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="flex gap-2">
            <select
              value={regionFilter}
              onChange={(e) => setRegionFilter(e.target.value as RegionFilter)}
              className="h-10 rounded-none border border-input bg-background px-3 text-sm"
            >
              <option value="all">All regions</option>
              <option value="MA">Massachusetts</option>
              <option value="DR">Dominican Republic</option>
            </select>
            <select
              value={pubFilter}
              onChange={(e) => setPubFilter(e.target.value as PubFilter)}
              className="h-10 rounded-none border border-input bg-background px-3 text-sm"
            >
              <option value="all">All</option>
              <option value="published">Published</option>
              <option value="draft">Draft</option>
            </select>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => void load()} disabled={loading} className="rounded-none">
              <RefreshCw className={`mr-2 h-4 w-4 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
            <Button variant="gold" onClick={openNew} className="rounded-none">
              <Plus className="mr-2 h-4 w-4" />
              New property
            </Button>
          </div>
        </div>

        <div className="border border-border bg-card">
          {loading ? (
            <div className="flex justify-center py-16">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : error ? (
            <div className="p-6 text-destructive">{error}</div>
          ) : filtered.length === 0 ? (
            <div className="p-12 text-center text-sm text-muted-foreground">
              No properties yet. Click "New property" to add one.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 text-left text-[11px] uppercase tracking-[0.12em] text-muted-foreground">
                  <tr>
                    <th className="px-4 py-3">Property</th>
                    <th className="px-4 py-3 hidden md:table-cell">Region</th>
                    <th className="px-4 py-3 hidden lg:table-cell">Status</th>
                    <th className="px-4 py-3">Order</th>
                    <th className="px-4 py-3">Flags</th>
                    <th className="px-4 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {filtered.map((r) => (
                    <tr key={r.id} className="hover:bg-muted/30">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          {r.hero_image_url ? (
                            <img src={r.hero_image_url} alt="" className="h-10 w-14 border border-border object-cover" />
                          ) : (
                            <div className="h-10 w-14 border border-border bg-muted" />
                          )}
                          <div>
                            <p className="font-medium text-foreground">{r.title}</p>
                            <p className="text-[11px] text-muted-foreground">{r.slug}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-muted-foreground hidden md:table-cell">{r.region}</td>
                      <td className="px-4 py-3 text-muted-foreground hidden lg:table-cell">{r.listing_status ?? "—"}</td>
                      <td className="px-4 py-3 text-muted-foreground">{r.sort_order}</td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          <span
                            className={`inline-flex items-center border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] ${
                              r.is_published
                                ? "bg-emerald-500/10 text-emerald-700 border-emerald-500/30"
                                : "bg-muted text-muted-foreground border-border"
                            }`}
                          >
                            {r.is_published ? "Published" : "Draft"}
                          </span>
                          {r.is_featured ? (
                            <span className="inline-flex items-center border border-accent/30 bg-accent/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-accent">
                              Featured
                            </span>
                          ) : null}
                          {r.show_on_homepage ? (
                            <span className="inline-flex items-center border border-blue-500/30 bg-blue-500/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-blue-700">
                              Home
                            </span>
                          ) : null}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex justify-end gap-2">
                          <Button size="sm" variant="outline" onClick={() => toggleFeatured(r)} className="rounded-none" title="Toggle featured">
                            <Star className={`h-3.5 w-3.5 ${r.is_featured ? "fill-accent text-accent" : ""}`} />
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => togglePublish(r)} className="rounded-none">
                            {r.is_published ? "Unpublish" : "Publish"}
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => openEdit(r)} className="rounded-none">
                            Edit
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <p className="text-xs text-muted-foreground">
          {filtered.length} of {rows.length} properties
        </p>
      </div>

      <Sheet open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <SheetContent className="w-full overflow-y-auto sm:max-w-3xl">
          {editing ? (
            <form onSubmit={handleSave}>
              <SheetHeader>
                <SheetTitle className="font-headline text-2xl">
                  {editing.id ? "Edit property" : "New property"}
                </SheetTitle>
                <SheetDescription>
                  Published properties appear on the public site. Use draft to keep work private.
                </SheetDescription>
              </SheetHeader>

              <div className="mt-6 space-y-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <Field label="Title *">
                    <Input
                      required
                      value={editing.title ?? ""}
                      onChange={(e) => {
                        const v = e.target.value;
                        setEditing((prev) =>
                          prev ? { ...prev, title: v, slug: slugTouched ? prev.slug : slugify(v) } : prev,
                        );
                      }}
                      className="rounded-none"
                    />
                  </Field>
                  <Field label="Slug *">
                    <Input
                      required
                      value={editing.slug ?? ""}
                      onChange={(e) => { setSlugTouched(true); updateField("slug", slugify(e.target.value)); }}
                      className="rounded-none"
                    />
                  </Field>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <Field label="Region *">
                    <select
                      required
                      value={editing.region ?? ""}
                      onChange={(e) => updateField("region", e.target.value)}
                      className="h-10 w-full rounded-none border border-input bg-background px-3 text-sm"
                    >
                      {REGIONS.map((r) => <option key={r} value={r}>{r}</option>)}
                    </select>
                  </Field>
                  <Field label="Listing status">
                    <select
                      value={editing.listing_status ?? ""}
                      onChange={(e) => updateField("listing_status", e.target.value)}
                      className="h-10 w-full rounded-none border border-input bg-background px-3 text-sm"
                    >
                      <option value="">—</option>
                      {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </Field>
                  <Field label="Property type">
                    <Input
                      value={editing.property_type ?? ""}
                      onChange={(e) => updateField("property_type", e.target.value)}
                      placeholder="Apartment, Villa, Single Family…"
                      className="rounded-none"
                    />
                  </Field>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <Field label="Price (display)">
                    <Input
                      value={editing.price ?? ""}
                      onChange={(e) => updateField("price", e.target.value)}
                      placeholder="From $175,750 USD"
                      className="rounded-none"
                    />
                  </Field>
                  <Field label="Price (numeric, for sorting)">
                    <Input
                      type="number"
                      value={(editing.price_numeric ?? "") as number | string}
                      onChange={(e) => updateField("price_numeric", (e.target.value === "" ? null : Number(e.target.value)) as never)}
                      className="rounded-none"
                    />
                  </Field>
                </div>

                <Field label="Location (display)">
                  <Input
                    value={editing.location ?? ""}
                    onChange={(e) => updateField("location", e.target.value)}
                    placeholder="Cabarete, Puerto Plata, Dominican Republic"
                    className="rounded-none"
                  />
                </Field>

                <details className="border border-border bg-muted/10 p-4">
                  <summary className="cursor-pointer text-xs uppercase tracking-[0.14em] text-muted-foreground">Address fields (optional)</summary>
                  <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                    <Field label="Address line 1">
                      <Input value={editing.address_line_1 ?? ""} onChange={(e) => updateField("address_line_1", e.target.value)} className="rounded-none" />
                    </Field>
                    <Field label="Address line 2">
                      <Input value={editing.address_line_2 ?? ""} onChange={(e) => updateField("address_line_2", e.target.value)} className="rounded-none" />
                    </Field>
                    <Field label="City">
                      <Input value={editing.city ?? ""} onChange={(e) => updateField("city", e.target.value)} className="rounded-none" />
                    </Field>
                    <Field label="State / region">
                      <Input value={editing.state_region ?? ""} onChange={(e) => updateField("state_region", e.target.value)} className="rounded-none" />
                    </Field>
                    <Field label="Country">
                      <Input value={editing.country ?? ""} onChange={(e) => updateField("country", e.target.value)} className="rounded-none" />
                    </Field>
                  </div>
                </details>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <Field label="Bedrooms">
                    <Input type="number" step="0.5" value={(editing.bedrooms ?? "") as number | string}
                      onChange={(e) => updateField("bedrooms", (e.target.value === "" ? null : Number(e.target.value)) as never)} className="rounded-none" />
                  </Field>
                  <Field label="Bathrooms">
                    <Input type="number" step="0.5" value={(editing.bathrooms ?? "") as number | string}
                      onChange={(e) => updateField("bathrooms", (e.target.value === "" ? null : Number(e.target.value)) as never)} className="rounded-none" />
                  </Field>
                  <Field label="Square feet">
                    <Input type="number" value={(editing.square_feet ?? "") as number | string}
                      onChange={(e) => updateField("square_feet", (e.target.value === "" ? null : Number(e.target.value)) as never)} className="rounded-none" />
                  </Field>
                  <Field label="Lot size">
                    <Input value={editing.lot_size ?? ""} onChange={(e) => updateField("lot_size", e.target.value)} className="rounded-none" />
                  </Field>
                </div>

                <Field label="Short description">
                  <Textarea rows={2} value={editing.short_description ?? ""} onChange={(e) => updateField("short_description", e.target.value)} className="rounded-none" />
                </Field>

                <Field label="Full description">
                  <Textarea rows={6} value={editing.description ?? ""} onChange={(e) => updateField("description", e.target.value)} className="rounded-none" />
                </Field>

                <Field label="Investment highlights (one per line or comma separated)">
                  <Textarea rows={3} value={highlightsStr} onChange={(e) => setHighlightsStr(e.target.value)} className="rounded-none" />
                </Field>
                <Field label="Amenities (one per line or comma separated)">
                  <Textarea rows={3} value={amenitiesStr} onChange={(e) => setAmenitiesStr(e.target.value)} className="rounded-none" />
                </Field>
                <Field label="Features (one per line or comma separated)">
                  <Textarea rows={3} value={featuresStr} onChange={(e) => setFeaturesStr(e.target.value)} className="rounded-none" />
                </Field>

                <Field label="Hero image">
                  <div className="space-y-2">
                    {editing.hero_image_url ? (
                      <div className="flex items-center gap-3">
                        <img src={editing.hero_image_url} alt="" className="h-20 w-28 border border-border object-cover" />
                        <Button type="button" size="sm" variant="outline" onClick={() => updateField("hero_image_url", "" as never)} className="rounded-none">
                          <X className="mr-1 h-3 w-3" /> Remove
                        </Button>
                      </div>
                    ) : null}
                    <Button type="button" variant="outline" onClick={() => setHeroPickerOpen(true)} className="rounded-none">
                      <ImageIcon className="mr-2 h-4 w-4" /> Choose from Media Library
                    </Button>
                    <Input value={editing.hero_image_url ?? ""} onChange={(e) => updateField("hero_image_url", e.target.value)} placeholder="Or paste a direct image URL" className="rounded-none" />
                  </div>
                </Field>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <Field label="CTA label">
                    <Input value={editing.cta_label ?? ""} onChange={(e) => updateField("cta_label", e.target.value)} placeholder="Request details" className="rounded-none" />
                  </Field>
                  <Field label="CTA URL (path or full URL)">
                    <Input value={editing.cta_url ?? ""} onChange={(e) => updateField("cta_url", e.target.value)} placeholder="/consultation or https://…" className="rounded-none" />
                  </Field>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <Field label="Sort order">
                    <Input
                      type="number"
                      value={editing.sort_order ?? 0}
                      onChange={(e) => updateField("sort_order", (Number(e.target.value) || 0) as never)}
                      className="rounded-none"
                    />
                  </Field>
                  <div className="space-y-2 pt-6 text-sm">
                    <label className="flex items-center gap-2">
                      <input type="checkbox" checked={!!editing.is_published} onChange={(e) => updateField("is_published", e.target.checked as never)} />
                      Published
                    </label>
                    <label className="flex items-center gap-2">
                      <input type="checkbox" checked={!!editing.is_featured} onChange={(e) => updateField("is_featured", e.target.checked as never)} />
                      Featured
                    </label>
                    <label className="flex items-center gap-2">
                      <input type="checkbox" checked={!!editing.show_on_homepage} onChange={(e) => updateField("show_on_homepage", e.target.checked as never)} />
                      Show on homepage
                    </label>
                  </div>
                </div>

                {/* Gallery */}
                {editing.id ? (
                  <div className="space-y-3 border border-border bg-muted/10 p-4">
                    <div className="flex items-center justify-between">
                      <p className="text-xs uppercase tracking-[0.14em] text-muted-foreground">Gallery images</p>
                      <Button type="button" size="sm" variant="outline" onClick={() => setGalleryPickerOpen(true)} className="rounded-none">
                        <Plus className="mr-1 h-3 w-3" /> Add image
                      </Button>
                    </div>
                    {galleryLoading ? (
                      <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
                    ) : gallery.length === 0 ? (
                      <p className="py-4 text-center text-xs text-muted-foreground">No gallery images yet.</p>
                    ) : (
                      <div className="space-y-2">
                        {gallery.map((img, i) => (
                          <div key={img.id} className="flex items-center gap-3 border border-border bg-card p-2">
                            <img src={img.image_url} alt={img.alt_text ?? ""} className="h-14 w-20 border border-border object-cover" />
                            <div className="flex-1 min-w-0">
                              <p className="truncate text-xs text-muted-foreground">{img.image_url}</p>
                              <Input
                                type="number"
                                value={img.sort_order}
                                onChange={(e) => setImageSortOrder(img, Number(e.target.value) || 0)}
                                className="mt-1 h-8 w-24 rounded-none text-xs"
                              />
                            </div>
                            <div className="flex flex-col gap-1">
                              <Button type="button" size="sm" variant="outline" disabled={i === 0} onClick={() => moveImage(img, -1)} className="h-7 rounded-none px-2">
                                <ArrowUp className="h-3 w-3" />
                              </Button>
                              <Button type="button" size="sm" variant="outline" disabled={i === gallery.length - 1} onClick={() => moveImage(img, 1)} className="h-7 rounded-none px-2">
                                <ArrowDown className="h-3 w-3" />
                              </Button>
                            </div>
                            <Button type="button" size="sm" variant="outline" onClick={() => removeGalleryImage(img)} className="rounded-none">
                              Remove
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                    <p className="text-[11px] text-muted-foreground">Removing here detaches the image from this property only — the file stays in the Media Library.</p>
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground">Save the property to add gallery images.</p>
                )}

                <div className="flex gap-2 pt-2">
                  <Button type="submit" variant="gold" disabled={saving} className="rounded-none">
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
                  </Button>
                  <Button type="button" variant="outline" onClick={() => setEditing(null)} className="rounded-none">
                    Close
                  </Button>
                </div>
              </div>
            </form>
          ) : null}
        </SheetContent>
      </Sheet>

      <MediaPicker
        open={heroPickerOpen}
        onOpenChange={setHeroPickerOpen}
        onSelect={(asset) => updateField("hero_image_url", asset.public_url as never)}
      />
      <MediaPicker
        open={galleryPickerOpen}
        onOpenChange={setGalleryPickerOpen}
        onSelect={(asset) => void addGalleryImage(asset.public_url)}
      />
    </AdminLayout>
  );
};

const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div>
    <label className="mb-2 block text-xs uppercase tracking-[0.14em] text-muted-foreground">
      {label}
    </label>
    {children}
  </div>
);

export default AdminProperties;
